import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Client, Note } from 'common/types';

interface State {
    initialized: boolean;
    client: Client | null;
}

const initialState: State = {
    initialized: false,
    client: null,
};

export const adminClientSlice = createSlice({
    name: 'adminClient',
    initialState,
    reducers: {
        initialize: (state: State, action: PayloadAction<Client>) => {
            state.initialized = true;
            state.client = {
                ...action.payload,
                properties:
                    action.payload.properties ?? state.client?.properties,
                propertyTotalCount:
                    action.payload.propertyTotalCount ??
                    state.client?.propertyTotalCount,
            };
        },

        reset: () => {
            return initialState;
        },

        setClient: (state: State, action: PayloadAction<Client>) => {
            state.client = {
                ...action.payload,
                properties:
                    action.payload.properties ?? state.client?.properties,
                propertyTotalCount:
                    action.payload.propertyTotalCount ??
                    state.client?.propertyTotalCount,
            };
        },

        setClientProperties: (
            state: State,
            action: PayloadAction<{
                properties: Client['properties'];
                propertyTotalCount: number;
            }>
        ) => {
            if (state.client) {
                state.client.properties = action.payload.properties;
                state.client.propertyTotalCount =
                    action.payload.propertyTotalCount;
            }
        },

        addNote: (state: State, action: PayloadAction<Note>) => {
            if (!state.client) {
                return;
            }

            const currentNotes = [...(state.client.notes ?? [])];
            const incomingNote = action.payload;

            state.client = {
                ...state.client,
                notes: [...currentNotes, incomingNote],
            };
        },

        updateNote: (state: State, action: PayloadAction<Note>) => {
            if (!state.client) {
                return;
            }

            const currentNotes = [...(state.client.notes ?? [])];
            const incomingNote = action.payload;

            const noteToUpdateIndex = currentNotes?.findIndex(
                note => note.id === incomingNote?.id
            );

            if (noteToUpdateIndex !== -1) {
                currentNotes[noteToUpdateIndex] = incomingNote;
            }

            state.client = {
                ...state.client,
                notes: currentNotes,
            };
        },

        updateNotificationPreferences: (state: State, action) => {
            if (!state.client) {
                return;
            }

            const notificationPreferences = action.payload;

            state.client = {
                ...state.client,
                notificationPreferences,
            };
        },

        updateReminderSetting: (state: State, action) => {
            if (!state.client) {
                return;
            }

            const clientSetting = action.payload;

            state.client = {
                ...state.client,
                enabledParentWorkOrderCheckInReminder:
                    clientSetting.enabledParentWorkOrderCheckInReminder,
                settings: clientSetting.settings,
            };
        },

        updateCompanyAddress: (state: State, action) => {
            if (!state.client) {
                return;
            }

            const {
                rawAddress,
                stdDeliveryLine,
                stdCity,
                stdRegion,
                stdPostalCode,
            } = action.payload;

            state.client = {
                ...state.client,
                address: {
                    ...state.client?.address,
                    rawAddress,
                    stdDeliveryLine,
                    stdCity,
                    stdRegion,
                    stdPostalCode,
                },
            };
        },
    },
});

export const {
    initialize,
    reset,
    setClient,
    setClientProperties,
    addNote,
    updateNote,
    updateCompanyAddress,
    updateNotificationPreferences,
    updateReminderSetting,
} = adminClientSlice.actions;

export default adminClientSlice.reducer;
